import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, requestPasswordReset, resetPassword, resendEmailVerification, verifyEmail, type RegisterCompanyInput } from "./api";
import { useAuth } from "./auth";
import { registrationAttribution,trackAuthEvent } from "./analytics";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnSuffix = returnToSuffix(params.get("returnTo"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [challengeToken,setChallengeToken]=useState("");const[code,setCode]=useState("");const[rememberDevice,setRememberDevice]=useState(true);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const result=challengeToken?(await auth.completeTwoFactor(challengeToken,code,rememberDevice),{}):await auth.login(email,password);
      if(result.challengeToken){setChallengeToken(result.challengeToken);return;}
      trackAuthEvent("login");navigate(safeReturnTo(params.get("returnTo")), { replace: true });
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout title="Welcome back" intro="Sign in to your courier company workspace.">
      <form onSubmit={submit}>
        {challengeToken?<><div className="alert alert-info py-2">We emailed a six-digit security code to your verified address.</div><Field label="Security or recovery code"><input className="form-control" inputMode="numeric" autoComplete="one-time-code" required minLength={6} maxLength={20} value={code} onChange={event=>setCode(event.target.value.trim())}/></Field><label className="form-check mb-3"><input className="form-check-input" type="checkbox" checked={rememberDevice} onChange={event=>setRememberDevice(event.target.checked)}/><span className="form-check-label">Trust this device for 90 days</span></label></>:<><Field label="Email address">
          <input className="form-control" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
        <Field label="Password">
          <input className="form-control" type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} />
        </Field>
        <p className="auth-help"><Link to="/forgot-password">Forgot your password?</Link></p></>}
        {params.get("reset")==="1"&&<div className="alert alert-success py-2" role="status">Password changed. Sign in with your new password.</div>}
        {params.get("verified")==="1"&&<div className="alert alert-success py-2" role="status">Email verified. You can now sign in.</div>}
        {error && <div className="alert alert-danger py-2" role="alert">{error}{error.startsWith("Verify your email")&&<><br/><Link to="/resend-verification">Resend activation email</Link></>}</div>}
        <button className="btn btn-primary w-100" type="submit" disabled={submitting}>
          {submitting ? "Checking…" : challengeToken?"Verify and sign in":"Sign in"}
        </button>
      </form>
      <p className="auth-switch mb-0">New to LoadSub? <Link to={`/register${returnSuffix}`}>Create your company account</Link></p>
    </AuthLayout>
  );
}

export function ForgotPasswordPage(){const[email,setEmail]=useState(""),[sent,setSent]=useState(false),[error,setError]=useState(""),[submitting,setSubmitting]=useState(false);async function submit(event:FormEvent){event.preventDefault();setSubmitting(true);setError("");try{await requestPasswordReset(email);setSent(true);}catch(caught){setError(messageFor(caught));}finally{setSubmitting(false);}}return <AuthLayout title="Reset your password" intro="We’ll email you a secure, single-use reset link.">{sent?<><div className="alert alert-success">If an active LoadSub account uses that email address, its reset link is on the way.</div><p className="auth-switch"><Link to="/login">Return to sign in</Link></p></>:<form onSubmit={submit}><Field label="Email address"><input className="form-control" type="email" autoComplete="email" required maxLength={320} value={email} onChange={event=>setEmail(event.target.value)}/></Field>{error&&<div className="alert alert-danger py-2">{error}</div>}<button className="btn btn-primary w-100" disabled={submitting}>{submitting?"Sending…":"Send reset link"}</button><p className="auth-switch"><Link to="/login">Return to sign in</Link></p></form>}</AuthLayout>}

export function ResetPasswordPage(){const[params]=useSearchParams(),navigate=useNavigate(),token=params.get("token")??"",[password,setPassword]=useState(""),[confirm,setConfirm]=useState(""),[error,setError]=useState(""),[submitting,setSubmitting]=useState(false);async function submit(event:FormEvent){event.preventDefault();setError("");if(password!==confirm){setError("The passwords do not match.");return;}setSubmitting(true);try{await resetPassword(token,password);navigate("/login?reset=1",{replace:true});}catch(caught){setError(messageFor(caught));}finally{setSubmitting(false);}}return <AuthLayout title="Choose a new password" intro="This link works once and expires 60 minutes after it was requested."><form onSubmit={submit}><Field label="New password" hint="At least 12 characters"><input className="form-control" type="password" autoComplete="new-password" required minLength={12} value={password} onChange={event=>setPassword(event.target.value)}/></Field><Field label="Confirm new password"><input className="form-control" type="password" autoComplete="new-password" required minLength={12} value={confirm} onChange={event=>setConfirm(event.target.value)}/></Field>{!token&&<div className="alert alert-danger py-2">This reset link is incomplete.</div>}{error&&<div className="alert alert-danger py-2">{error}</div>}<button className="btn btn-primary w-100" disabled={submitting||!token}>{submitting?"Saving…":"Set new password"}</button></form></AuthLayout>}

export function VerifyEmailPage(){const[params]=useSearchParams(),token=params.get("token")??"",[verified,setVerified]=useState(false),[error,setError]=useState(""),[submitting,setSubmitting]=useState(false);async function activate(){setSubmitting(true);setError("");try{await verifyEmail(token);setVerified(true);}catch(caught){setError(messageFor(caught));}finally{setSubmitting(false);}}return <AuthLayout title="Verify your email" intro="Confirm your email address to activate your LoadSub account.">{verified?<><div className="alert alert-success">Your email address is verified. Your account is ready.</div><p className="auth-switch"><Link to="/login?verified=1">Continue to sign in</Link></p></>:<>{!token&&<div className="alert alert-danger">This activation link is incomplete.</div>}{error&&<div className="alert alert-danger">{error}</div>}<button className="btn btn-primary w-100" type="button" disabled={!token||submitting} onClick={()=>void activate()}>{submitting?"Verifying…":"Verify email address"}</button></>}</AuthLayout>}

export function ResendVerificationPage(){const[email,setEmail]=useState(""),[sent,setSent]=useState(false),[submitting,setSubmitting]=useState(false);async function submit(event:FormEvent){event.preventDefault();setSubmitting(true);try{await resendEmailVerification(email);setSent(true);}finally{setSubmitting(false);}}return <AuthLayout title="Resend activation email" intro="Request a fresh, single-use activation link.">{sent?<><div className="alert alert-success">If that address belongs to an unverified account, a new activation email is on the way.</div><p className="auth-switch"><Link to="/login">Return to sign in</Link></p></>:<form onSubmit={submit}><Field label="Email address"><input className="form-control" type="email" required maxLength={320} value={email} onChange={event=>setEmail(event.target.value)}/></Field><button className="btn btn-primary w-100" disabled={submitting}>{submitting?"Sending…":"Send activation link"}</button></form>}</AuthLayout>}

export function RegisterPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnSuffix = returnToSuffix(params.get("returnTo"));
  const attribution=registrationAttribution(params);
  const [form, setForm] = useState<RegisterCompanyInput>({
    email: "",
    password: "",
    tradingName: "",
    legalName: "",
    businessType: "company",
    telephone: "",
    ...(params.get("managed")==="1"?{accountScope:"restricted" as const}:{})
    ,...(attribution?{attribution}:{})
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [registered,setRegistered]=useState(false);
  const [activationSent,setActivationSent]=useState(false);

  function update<K extends keyof RegisterCompanyInput>(key: K, value: RegisterCompanyInput[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (form.password !== confirmPassword) {
      setError("The passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const result=await auth.registerCompany(form);
      trackAuthEvent("sign_up");
      if(result.verificationRequired){setActivationSent(result.emailSent);setRegistered(true);}else navigate(safeReturnTo(params.get("returnTo")), { replace: true });
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  if(registered)return <AuthLayout title="Check your email" intro="Activate your LoadSub account before signing in."><div className={`alert ${activationSent?"alert-success":"alert-warning"}`}>{activationSent?"We sent a secure activation link to your email address. It expires in 24 hours.":"Your account was created, but the activation email could not be delivered."}</div><button className="btn btn-outline-primary w-100" type="button" onClick={()=>void resendEmailVerification(form.email).then(()=>setActivationSent(true))}>Resend activation email</button><p className="auth-switch"><Link to="/login">Return to sign in</Link></p></AuthLayout>;

  return (
    <AuthLayout title="Create your workspace" intro={params.get("managed")==="1"?"Create your company account for this managed subcontractor network.":"Create your independent LoadSub company account."}>
      <form onSubmit={submit}>
        <Field label="Company or trading name">
          <input className="form-control" autoComplete="organization" required maxLength={160} value={form.tradingName} onChange={(event) => update("tradingName", event.target.value)} />
        </Field>
        <Field label="Legal name" hint="The registered legal entity or proprietor name">
          <input className="form-control" autoComplete="organization" required maxLength={200} value={form.legalName} onChange={(event) => update("legalName", event.target.value)} />
        </Field>
        <div className="row g-3">
          <div className="col-md-6">
            <Field label="Business type">
              <select className="form-select" value={form.businessType} onChange={(event) => update("businessType", event.target.value as RegisterCompanyInput["businessType"])}>
                <option value="company">Limited company</option>
                <option value="sole_trader">Sole trader</option>
                <option value="partnership">Partnership</option>
                <option value="other">Other</option>
              </select>
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Telephone">
              <input className="form-control" type="tel" autoComplete="tel" required maxLength={40} value={form.telephone} onChange={(event) => update("telephone", event.target.value)} />
            </Field>
          </div>
        </div>
        <Field label="Email address">
          <input className="form-control" type="email" autoComplete="email" required maxLength={320} value={form.email} onChange={(event) => update("email", event.target.value)} />
        </Field>
        <div className="row g-3">
          <div className="col-md-6">
            <Field label="Password" hint="At least 12 characters">
              <input className="form-control" type="password" autoComplete="new-password" required minLength={12} value={form.password} onChange={(event) => update("password", event.target.value)} />
            </Field>
          </div>
          <div className="col-md-6">
            <Field label="Confirm password">
              <input className="form-control" type="password" autoComplete="new-password" required minLength={12} value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} />
            </Field>
          </div>
        </div>
        {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
        <button className="btn btn-primary w-100" type="submit" disabled={submitting}>
          {submitting ? "Creating account…" : "Create company account"}
        </button>
      </form>
      <p className="auth-switch mb-0">Already registered? <Link to={`/login${returnSuffix}`}>Sign in</Link></p>
    </AuthLayout>
  );
}

function AuthLayout({ title, intro, children }: { title: string; intro: string; children: React.ReactNode }) {
  return (
    <main className="auth-page">
      <section className="auth-card">
        <div className="auth-brand">
          <img src="/logo-mark.svg" alt="" />
          <span>LoadSub</span>
        </div>
        <h1>{title}</h1>
        <p className="text-secondary">{intro}</p>
        {children}
      </section>
    </main>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="form-label auth-field">
      <span>{label}</span>
      {children}
      {hint && <small className="form-text">{hint}</small>}
    </label>
  );
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Unable to reach LoadSub. Please try again.";
}

function safeReturnTo(value: string | null): string {
  return value?.startsWith("/") && !value.startsWith("//") ? value : "/loads";
}

function returnToSuffix(value: string | null): string {
  const safe = safeReturnTo(value);
  return safe === "/loads" ? "" : `?returnTo=${encodeURIComponent(safe)}`;
}
